import {
  FormStyle,
  getLocaleDayNames,
  getLocaleMonthNames,
  TranslationWidth,
} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChange,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Breakpoints } from '@rd-lib/breakpoints';
import { RTL_LOCALE_CODES } from '@rd-lib/rtl-locales';
import { LocaleFacade } from '@rd/core/facades/locale-facade/locale.facade';
import { DateService } from '@rd/core/services/date/date.service';
import { DomService } from '@rd/core/services/dom/dom.service';
import { WindowResizeService } from '@rd/core/services/window-resize/window-resize.service';
import { Locale } from '@rd/types';
import Pikaday from 'pikaday';
import { Subscription } from 'rxjs';

@Component({
  selector: 'rd-auto-redemption-date-picker',
  templateUrl: './auto-redemption-date-picker.component.html',
  styleUrls: ['./auto-redemption-date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutoRedemptionDatePickerComponent),
      multi: true,
    },
  ],
})
export class AutoRedemptionDatePickerComponent
  implements OnDestroy, OnChanges, AfterViewInit, OnInit, ControlValueAccessor
{
  @HostBinding('class.rd-auto-redemption-date-picker')
  componentClass = true;

  onChange: (value: Date) => void;
  onTouched: () => void;

  @Input() defaultDate?: Date;
  @Input() maxDate: Date;
  @Input() minDate: Date;
  @Input() numberOfMonths?: number;

  resizeSubscription$: Subscription;

  localeSelectedCode = 'en';

  isTabletViewport: boolean;

  format: 'll';

  @ViewChild('textInput') textInput: ElementRef;
  @ViewChild('datepickerContainer') datepickerContainer: ElementRef;

  private datePicker: Pikaday;

  constructor(
    private windowResizeService: WindowResizeService,
    private domService: DomService,
    private localeFacade: LocaleFacade,
    private dateService: DateService,
  ) {}

  writeValue(obj: Date): void {
    /**
     * Second param `true` prevents triggering onSelect callback (avoids circular updates).
     */
    if (this.datePicker && obj) {
      this.datePicker.setDate(obj, true);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  ngOnInit(): void {
    this.setupSubscriptions();
    this.domService.preventArrowKeysScroll();
  }

  ngAfterViewInit(): void {
    this.detectViewportSize();
    this.initializeDatePicker();
  }

  ngOnDestroy(): void {
    this.destroyDatePicker();

    if (this.resizeSubscription$) {
      this.resizeSubscription$.unsubscribe();
    }

    this.domService.releasePreventArrowKeysScroll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.datePicker) {
      if (
        this.hasInputChanged(changes.selectionState) ||
        this.hasInputChanged(changes.startDate) ||
        this.hasInputChanged(changes.endDate)
      ) {
        this.updateDateRanges();
      }

      if (this.hasInputChanged(changes.minDate)) {
        this.updateMinDate();
      }

      if (this.hasInputChanged(changes.maxDate)) {
        this.updateMaxDate();
      }
    }
  }

  setupSubscriptions(): void {
    this.resizeSubscription$ =
      this.windowResizeService.windowResizeObservable.subscribe(() => {
        this.detectViewportSize();
        this.redrawDatePicker();
      });

    this.localeFacade.selectedLocale$.subscribe((locale: Locale) => {
      if (locale) {
        this.updateLocaleSelected(locale);
        this.redrawDatePicker();
      }
    });
  }

  updateLocaleSelected(locale: Locale): void {
    this.localeSelectedCode = locale.code;
  }

  redrawDatePicker(): void {
    if (this.datePicker) {
      this.destroyDatePicker();
      this.initializeDatePicker();
      this.updateDateRanges();
    }
  }

  detectViewportSize(): void {
    this.isTabletViewport = window.innerWidth < Breakpoints.DESKTOP;
  }

  destroyDatePicker(): void {
    if (this.datePicker) {
      this.datePicker.destroy();
    }
  }

  initializeDatePicker(): void {
    this.datePicker = new Pikaday(this.datePickerOptions());
  }

  datePickerOptions(): any {
    return {
      field: this.textInput.nativeElement,
      container: this.datepickerContainer.nativeElement,
      defaultDate: this.defaultDate,
      setDefaultDate: Boolean(this.defaultDate),
      minDate: this.calendarMinDate(),
      maxDate: this.calendarMaxDate(),
      yearRange: 1,
      format: this.format,
      bound: false,
      yearSuffix: '',
      numberOfMonths: 1,
      onSelect: (newDate: Date) => {
        this.onSelectDate(newDate);
      },
      firstDay: 1,
      showDaysInNextAndPreviousMonths: true,
      onDraw: () => {
        setTimeout(() => this.initFocusEventListener(), 1);
      },
      ...this.getI18nSettings(),
    };
  }

  getI18nSettings(): { i18n: any; isRTL: boolean } {
    return {
      i18n: {
        months: getLocaleMonthNames(
          this.localeSelectedCode,
          FormStyle.Format,
          TranslationWidth.Wide,
        ),
        weekdays: getLocaleDayNames(
          this.localeSelectedCode,
          FormStyle.Format,
          TranslationWidth.Abbreviated,
        ),
        weekdaysShort: getLocaleDayNames(
          this.localeSelectedCode,
          FormStyle.Format,
          TranslationWidth.Narrow,
        ),
      },
      isRTL: this.isRTL(this.localeSelectedCode),
    };
  }

  onSelectDate(newDate: Date): void {
    this.onChange(newDate);
  }

  isRTL(locale: string): boolean {
    return RTL_LOCALE_CODES.includes(locale);
  }

  updateDateRanges(): void {
    const updatedDate = this.setToStartOfDay(this.datePicker.getDate());
    this.datePicker.setDate(updatedDate);
    this.datePicker.hide();
    this.datePicker.show();
    if (this.onChange) {
      this.onChange(updatedDate);
    }
  }

  setToStartOfDay(date: Date | null): Date {
    if (date) {
      const clonedDate = new Date(+date);
      clonedDate.setHours(0, 0, 0, 0);
      return clonedDate;
    }

    return new Date();
  }

  updateMinDate(): void {
    this.datePicker.setMinDate(this.minDate);
    this.datePicker.hide();
    this.datePicker.show();
  }

  updateMaxDate(): void {
    this.datePicker.setMaxDate(this.maxDate);
    this.datePicker.hide();
    this.datePicker.show();
  }

  calendarMinDate(): Date {
    return this.minDate;
  }

  calendarMaxDate(): Date {
    return this.maxDate;
  }

  hasInputChanged(change: SimpleChange): boolean {
    return (
      change &&
      change.currentValue &&
      change.currentValue !== change.previousValue
    );
  }

  private initFocusEventListener(): void {
    const pikaTitle =
      this.datepickerContainer.nativeElement.querySelector('.pika-title');

    if (pikaTitle) {
      pikaTitle.removeAttribute('aria-live');
    }
    this.setupPikaChangeMonthButtons();
    this.setupPikaHeader();
    this.setupPikaButtons();
  }

  private setupPikaChangeMonthButtons(): void {
    const element = this.datepickerContainer.nativeElement;
    const pikaPrev = element.querySelector('.pika-prev');
    const pikaNext = element.querySelector('.pika-next');

    if (pikaPrev?.classList.contains('is-disabled')) {
      pikaPrev.setAttribute('aria-disabled', true);
    }

    if (pikaNext?.classList.contains('is-disabled')) {
      pikaNext.setAttribute('aria-disabled', true);
    }
  }

  private setupPikaHeader(): void {
    const element = this.datepickerContainer.nativeElement;
    // setup pika header
    const pikaHeading = element.querySelector('.pika-title');
    pikaHeading?.removeAttribute('role');

    const pikaMonthSelect = element.querySelector('.pika-select-month');
    pikaMonthSelect?.setAttribute('aria-hidden', true);

    const pikaYearSelect = element.querySelector('.pika-select-year');
    pikaYearSelect?.setAttribute('aria-hidden', true);

    // setup table headers
    const pikaHeaders = element.querySelectorAll('.pika-table abbr');
    pikaHeaders.forEach((el: HTMLElement) => {
      el.setAttribute('aria-hidden', 'true');
    });

    const pikaTable = element.querySelector('table.pika-table');
    pikaTable?.setAttribute('role', 'presentation');
  }

  private setupPikaButtons(): void {
    const element = this.datepickerContainer.nativeElement;
    // setup pikaday aria d
    const pikaButtons = element.querySelectorAll('.pika-button.pika-day');
    pikaButtons.forEach((el: HTMLElement) => {
      const pikaData = el.dataset;
      const buttonDate = new Date(
        // @ts-ignore
        pikaData.pikaYear,
        pikaData.pikaMonth,
        pikaData.pikaDay,
      );
      let ariaDateString = this.dateService
        .dayjs(buttonDate)
        .format('dddd, MMM D, yyyy');

      if (el.parentElement?.classList.contains('is-disabled')) {
        ariaDateString = `${ariaDateString}, date disabled`;
      }
      el.setAttribute('aria-label', ariaDateString);
    });
  }
}
